<script setup lang="ts">
import { computed } from "vue";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import UserAvatar from "@/components/ui/UserAvatar/UserAvatar.vue";
import { useUser } from "@/composables/useUser";
import { maxVisibleUsersOnBoard } from "@/model/Settings";
import type { BoardUser } from "@/model/user";

const props = defineProps<{ users: BoardUser[] }>();

const { userName } = useUser();

const visibleUsers = computed(() => props.users.slice(0, maxVisibleUsersOnBoard));
const userNames = computed(() => props.users.map((user) => user.name).join(", "));
const more = computed(() => props.users.length - maxVisibleUsersOnBoard);
</script>

<template>
  <BaseTooltip
    position="bottom"
    class="board-users"
    interactive
    tabindex="0"
    :aria-label="$t('boardUsers.currentUsersOnBoard', { userNames })"
  >
    <div data-testid="board-users-preview" class="users-preview" tabindex="-1" aria-hidden="true">
      <UserAvatar v-for="user in visibleUsers" :key="user.id" :user />
      <span v-if="more > 0" class="more">
        {{ $t("boardUsers.moreUsers", { more }) }}
      </span>
    </div>
    <template #content>
      <div class="users-list">
        <h3>{{ $t("boardUsers.activeMembers") }}</h3>
        <ul class="scrollable scrollable-relative">
          <li v-for="user in users" :key="user.id">
            <UserAvatar :user size="small" />
            {{ userName(user) }}
          </li>
        </ul>
      </div>
    </template>
  </BaseTooltip>
</template>

<style lang="scss">
@use "@/styles/tooltip-users-list";
@use "@/styles/variables";
@use "@/styles/variables/colors";

.board-users {
  outline: revert;
}

.users-preview {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px variables.$top-menu-item-spacing;

  .more,
  .avatar:not(:first-child) {
    margin-left: -7px;
  }

  .more {
    border-radius: 50%;
    background-color: colors.$text-subtle;
    display: flex;
    align-items: center;
    justify-content: center;
    color: colors.$text-inverse;
    width: 24px;
    height: 24px;
    font-size: 12px;
  }
}
</style>
